import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { listMyLscJoinRequests, LscApiError } from '../api';
import { formatJoinRequestDate } from '../joinRequestQueue';
import {
  myJoinRequestBadgeClass,
  myJoinRequestBadgeLabel,
  myJoinRequestBadgeStatus,
  myJoinRequestShowsOpenLeague,
  myJoinRequestStatusMessage,
} from '../myJoinRequests';
import type { LscMyJoinRequest } from '../types';
import { lscPath } from '../routing';
import ErrorBanner from './ErrorBanner';
import LoadingState from './LoadingState';
import {
  JOIN_MY_REQUESTS_DETAIL,
  JOIN_MY_REQUESTS_EMPTY,
  JOIN_MY_REQUESTS_HEADLINE,
} from '../joinMemberCopy';

interface MyJoinRequestsPanelProps {
  /** When true, show a compact empty state without join CTA (parent may provide actions). */
  compactEmpty?: boolean;
}

export function MyJoinRequestCard({ item }: { item: LscMyJoinRequest }) {
  const badgeStatus = myJoinRequestBadgeStatus(item);
  const badgeLabel = myJoinRequestBadgeLabel(badgeStatus);
  const message = myJoinRequestStatusMessage(item);
  const showOpenLeague = myJoinRequestShowsOpenLeague(item);
  const submittedAt = item.requested_at || item.created_at;

  return (
    <li
      className="card space-y-3"
      data-testid="my-join-request-card"
      data-display-status={badgeStatus}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-white truncate">
            {item.league_name || 'League workspace'}
          </h3>
          <p className="text-xs text-secondary-500 mt-1">
            Submitted {formatJoinRequestDate(submittedAt)}
          </p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${myJoinRequestBadgeClass(
            badgeStatus
          )}`}
          data-testid="my-join-request-badge"
        >
          {badgeLabel}
        </span>
      </div>

      <p className="text-sm text-secondary-300" data-testid="my-join-request-message">
        {message}
      </p>

      {item.rejection_reason && badgeStatus === 'rejected' && (
        <p className="text-xs text-secondary-500" data-testid="my-join-request-rejection-reason">
          Note: {item.rejection_reason}
        </p>
      )}

      {showOpenLeague && item.league_id && (
        <Link
          to={lscPath(`/leagues/${item.league_id}`)}
          className="btn-primary text-sm inline-flex"
          data-testid="open-league-cta"
        >
          Open league
        </Link>
      )}
    </li>
  );
}

const MyJoinRequestsPanel: React.FC<MyJoinRequestsPanelProps> = ({ compactEmpty = false }) => {
  const { user, refreshUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<LscMyJoinRequest[]>([]);

  const loadRequests = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listMyLscJoinRequests();
      setRequests(data.requests ?? []);
    } catch (err) {
      if (err instanceof LscApiError && err.status === 401) {
        await refreshUser();
      }
      setError(err instanceof LscApiError ? err.message : 'Could not load your join requests.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [refreshUser, user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    void loadRequests();
  }, [loadRequests, user?.uid]);

  if (loading) {
    return <LoadingState message="Loading your join requests..." />;
  }

  return (
    <section className="space-y-4" data-testid="my-join-requests-panel">
      <div>
        <h2 className="text-lg font-semibold text-white">{JOIN_MY_REQUESTS_HEADLINE}</h2>
        <p className="text-sm text-secondary-400 mt-1">{JOIN_MY_REQUESTS_DETAIL}</p>
      </div>

      {error && <ErrorBanner message={error} />}

      {!requests.length ? (
        <div className="card text-sm text-secondary-400" data-testid="my-join-requests-empty">
          <p>{JOIN_MY_REQUESTS_EMPTY}</p>
          {!compactEmpty && (
            <Link to={lscPath('/join')} className="btn-primary text-sm mt-4 inline-flex">
              Enter invite code
            </Link>
          )}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="my-join-requests-list">
          {requests.map((item) => (
            <MyJoinRequestCard
              key={`${item.league_id}-${item.request_id || item.id}`}
              item={item}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

export default MyJoinRequestsPanel;
